import { clearDragItem, readDragItem, writeDragItem, type BlockPreset } from '@/components/builder/drag-data';
import {
  NodeContextMenu,
  type ContextMenuState,
  type NodeActionHandlers,
} from '@/components/builder/node-actions';
import { cn } from '@/lib/cn';
import { isContainer, isDescendant, nodeLabel, type AuthoringDoc } from '@personalizehq/editor';
import type { AuthoringBlockNode as BlockNode } from '@personalizehq/schemas';
import {
  Box,
  ChevronDown,
  ChevronRight,
  Columns3,
  ChevronsDownUp,
  ChevronsUpDown,
  Focus,
  GripVertical,
  Image,
  LayoutGrid,
  Minus,
  MousePointerClick,
  MoreHorizontal,
  Repeat,
  Search,
  ScrollText,
  SlidersHorizontal,
  Square,
  Type,
  Video,
} from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

// The doc IS a tree, so the layers panel is a render of it — no parallel model, nothing to keep in
// sync. It walks the EDITABLE tree only: `findNode` deliberately does not descend into `fallback`
// or `repeat.template`, and neither does this. Those are separate authoring surfaces (E5), and a
// selection that landed inside one from here would be a silent structural bug.
//
// C3: this is the ACCESSIBLE authoring path (the canvas stays a visual, click-select surface). So the
// tree is a real WAI-ARIA `tree`: ONE tab stop (roving tabindex), arrow keys to move/expand/collapse,
// F2 to rename, ⌘/⌥+↑↓ to reorder, and `aria-level/posinset/setsize/expanded` on every row. That
// needs the flattened visible order, so expand/focus/rename state lives HERE, not per-row.

export const BLOCK_ICON: Record<BlockNode['block'], typeof Box> = {
  stack: Columns3,
  box: Square,
  scroll: ScrollText,
  grid: LayoutGrid,
  text: Type,
  image: Image,
  video: Video,
  button: MousePointerClick,
  spacer: Minus,
  custom: Box,
  progress: SlidersHorizontal,
  repeat: Repeat,
};

interface LayersTreeProps {
  root: BlockNode;
  labels: AuthoringDoc['labels'];
  selection: readonly string[];
  selectionAnchor?: string | null | undefined;
  onSelect: (id: string, mode?: 'replace' | 'toggle' | 'range') => void;
  onRename: (id: string, name: string) => void;
  onInsert: (id: string) => void;
  /** F2: reorder a node within its parent by ±1. F3: duplicate. Plus delete. All three were built
   *  and fixture-tested in `@personalizehq/editor` (`moveNode`/`duplicateNode`) with ZERO UI callers
   *  until now — a block could not be reordered or duplicated at all, only deleted and rebuilt. */
  onReorder: (id: string, delta: -1 | 1) => void;
  onDuplicate: (id: string) => void;
  onWrap: (id: string, preset: BlockPreset) => void;
  onDelete: (id: string) => void;
  onCopy?: ((id: string) => void) | undefined;
  onCut?: ((id: string) => void) | undefined;
  onPaste?: ((id: string) => void) | undefined;
  onMove: (id: string, parentId: string, index: number) => void;
}

type TreeDrop = { rowId: string; parentId: string; index: number; position: 'before' | 'inside' | 'after' };

/** One visible row, in render order. `parentId === null` and `index === -1` mark the root, which has
 *  no verbs and cannot be moved. Computed from the tree + the collapsed set so keyboard nav and the
 *  `posinset/setsize` ARIA both read the same order. */
type FlatRow = {
  node: BlockNode;
  parentId: string | null;
  /** Actual tree position — mutations and DnD must never use the filtered list's index. */
  index: number;
  siblingCount: number;
  /** Filtered position — WAI-ARIA describes the rows the user can currently perceive. */
  visibleIndex: number;
  visibleSiblingCount: number;
  depth: number;
};

function flattenVisible(
  root: BlockNode,
  collapsed: ReadonlySet<string>,
  included?: ReadonlySet<string>
): FlatRow[] {
  const out: FlatRow[] = [];
  const walk = (
    node: BlockNode,
    parentId: string | null,
    index: number,
    siblingCount: number,
    visibleIndex: number,
    visibleSiblingCount: number,
    depth: number
  ): void => {
    if (included && !included.has(node.id)) return;
    out.push({ node, parentId, index, siblingCount, visibleIndex, visibleSiblingCount, depth });
    const children = isContainer(node) ? node.children : [];
    // A search result must never stay hidden behind yesterday's collapsed state.
    if (children.length > 0 && (!collapsed.has(node.id) || included)) {
      const visibleChildren = children
        .map((child, index) => ({ child, index }))
        .filter(({ child }) => !included || included.has(child.id));
      visibleChildren.forEach(({ child, index }, visibleIndex) =>
        walk(child, node.id, index, children.length, visibleIndex, visibleChildren.length, depth + 1)
      );
    }
  };
  walk(root, null, -1, 1, 0, 1, 0);
  return out;
}

type LayerSearch = { included: ReadonlySet<string>; matches: ReadonlySet<string> };

function searchPaths(root: BlockNode, labels: AuthoringDoc['labels'], query: string): LayerSearch | undefined {
  const needle = query.trim().toLocaleLowerCase();
  if (!needle) return undefined;

  const included = new Set<string>();
  const matches = new Set<string>();
  const walk = (node: BlockNode): boolean => {
    const children = isContainer(node) ? node.children : [];
    let childMatch = false;
    for (const child of children) childMatch = walk(child) || childMatch;
    const own = `${nodeLabel(node, labels)} ${node.block} ${node.id}`.toLocaleLowerCase().includes(needle);
    if (own) matches.add(node.id);
    if (own || childMatch) included.add(node.id);
    return own || childMatch;
  };
  walk(root);
  return { included, matches };
}

function containerIds(root: BlockNode): Set<string> {
  const ids = new Set<string>();
  const walk = (node: BlockNode): void => {
    if (!isContainer(node) || node.children.length === 0) return;
    ids.add(node.id);
    node.children.forEach(walk);
  };
  walk(root);
  return ids;
}

function ancestorIds(root: BlockNode, id: string): Set<string> {
  const ancestors = new Set<string>();
  const walk = (node: BlockNode): boolean => {
    if (node.id === id) return true;
    if (!isContainer(node)) return false;
    for (const child of node.children) {
      if (walk(child)) {
        ancestors.add(node.id);
        return true;
      }
    }
    return false;
  };
  walk(root);
  return ancestors;
}

export function LayersTree({
  root,
  labels,
  selection,
  selectionAnchor,
  onSelect,
  onRename,
  onInsert,
  onReorder,
  onDuplicate,
  onWrap,
  onDelete,
  onCopy,
  onCut,
  onPaste,
  onMove,
}: LayersTreeProps): React.JSX.Element {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [drop, setDrop] = useState<TreeDrop | null>(null);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [query, setQuery] = useState('');
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const rowRefs = useRef(new Map<string, HTMLDivElement>());

  const search = useMemo(() => searchPaths(root, labels, query), [root, labels, query]);
  const included = search?.included;
  const flat = useMemo(() => flattenVisible(root, collapsed, included), [root, collapsed, included]);
  const order = useMemo(() => flat.map((row) => row.node.id), [flat]);
  const allContainers = useMemo(() => containerIds(root), [root]);
  const searchActive = search !== undefined;
  const allExpanded = [...allContainers].every((id) => !collapsed.has(id));
  const allCollapsed = [...allContainers].every((id) => collapsed.has(id));

  const handlers: NodeActionHandlers = {
    onInsert,
    onReorder,
    onDuplicate,
    onWrap,
    onDelete,
    ...(onCopy ? { onCopy } : {}),
    ...(onCut ? { onCut } : {}),
    ...(onPaste ? { onPaste } : {}),
  };

  // Exactly ONE row is tabbable. Prefer the last keyboard focus, fall back to the selection, then the
  // root — and only if it is still visible, so a collapse never strands the tab stop on a hidden row.
  const activeId =
    (focusedId && order.includes(focusedId) && focusedId) ||
    (selectionAnchor && order.includes(selectionAnchor) && selectionAnchor) ||
    (selection.length > 0 && order.includes(selection[selection.length - 1]!) && selection[selection.length - 1]) ||
    root.id;

  const focusRow = (id: string): void => {
    setFocusedId(id);
    // Programmatic focus works regardless of tabindex, and every arrow target is already visible.
    rowRefs.current.get(id)?.focus();
  };

  const setOpen = (id: string, open: boolean): void =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (open) next.delete(id);
      else next.add(id);
      return next;
    });

  const revealSelection = (): void => {
    const anchor = selectionAnchor ?? selection[selection.length - 1];
    if (!anchor) return;
    const ancestors = ancestorIds(root, anchor);
    setQuery('');
    setCollapsed((prev) => {
      const next = new Set(prev);
      ancestors.forEach((id) => next.delete(id));
      return next;
    });
    setFocusedId(anchor);
    // React commits the expanded/search-cleared tree at the end of this event. Focus in the next
    // frame, when the selected row's ref is guaranteed to exist.
    window.requestAnimationFrame(() => {
      const row = rowRefs.current.get(anchor);
      row?.scrollIntoView?.({ block: 'nearest' });
      row?.focus();
    });
  };

  const onKeyNav = (row: FlatRow, e: React.KeyboardEvent<HTMLDivElement>): void => {
    const { node, parentId } = row;
    const isRoot = parentId === null;
    const container = isContainer(node) && node.children.length > 0;
    const open = !collapsed.has(node.id);
    const i = order.indexOf(node.id);

    if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
      e.preventDefault();
      if (!selection.includes(node.id)) onSelect(node.id);
      const rect = e.currentTarget.getBoundingClientRect();
      setContextMenu({ id: node.id, x: rect.left + Math.min(rect.width, 180), y: rect.top + rect.height });
      return;
    }

    // Reorder MUST win over plain arrow nav, so check the modifier first. Within-parent only; the
    // verb's own `disabled` bounds are mirrored here (index, siblingCount).
    if ((e.metaKey || e.altKey) && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      if (isRoot) return;
      if (e.key === 'ArrowUp' && row.index > 0) onReorder(node.id, -1);
      if (e.key === 'ArrowDown' && row.index < row.siblingCount - 1) onReorder(node.id, 1);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (i < order.length - 1) {
          const target = order[i + 1]!;
          focusRow(target);
          if (e.shiftKey) onSelect(target, 'range');
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (i > 0) {
          const target = order[i - 1]!;
          focusRow(target);
          if (e.shiftKey) onSelect(target, 'range');
        }
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (container && !open) setOpen(node.id, true);
        else if (container && open && i < order.length - 1) focusRow(order[i + 1]!); // first child is next in order
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (container && open) setOpen(node.id, false);
        else if (parentId) focusRow(parentId);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        onSelect(node.id, e.shiftKey ? 'range' : e.metaKey || e.ctrlKey ? 'toggle' : 'replace');
        break;
      case 'F2':
        if (!isRoot) {
          e.preventDefault();
          setRenamingId(node.id);
        }
        break;
      default:
        break;
    }
  };

  const targetFor = (row: FlatRow, e: React.DragEvent<HTMLDivElement>): TreeDrop | null => {
    const { node, parentId, index } = row;
    const item = readDragItem(e.dataTransfer);
    const id = item?.type === 'node' ? item.id : draggedId;
    if (!id || id === root.id) return null;

    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientY - rect.top) / rect.height;
    const target: TreeDrop =
      parentId === null
        ? { rowId: node.id, parentId: node.id, index: isContainer(node) ? node.children.length : 0, position: 'inside' }
        : ratio < 1 / 3
          ? { rowId: node.id, parentId, index, position: 'before' }
          : ratio > 2 / 3 || !isContainer(node)
            ? { rowId: node.id, parentId, index: index + 1, position: 'after' }
            : { rowId: node.id, parentId: node.id, index: node.children.length, position: 'inside' };

    return isDescendant(root, id, target.parentId) ? null : target;
  };

  const clearDrag = (): void => {
    clearDragItem();
    setDraggedId(null);
    setDrop(null);
  };

  // An empty root is a lone tree row that reads like a dead end. Say what to do — the canvas already
  // draws its own "empty box" placeholder, but the author is looking at this panel (C4).
  const rootEmpty = isContainer(root) && root.children.length === 0;

  return (
    <>
    <div className="space-y-1.5 border-b px-2 py-2">
      <label className="relative block">
        <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          aria-label="Search layers"
          placeholder="Search layers"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className="h-8 w-full rounded-md border bg-background pl-7 pr-2 text-xs outline-none placeholder:text-muted-foreground focus:border-indigo-500"
        />
      </label>
      <div role="toolbar" aria-label="Layer view controls" className="flex items-center gap-1">
        <ViewButton label="Expand all" disabled={searchActive || allExpanded} onClick={() => setCollapsed(new Set())}>
          <ChevronsUpDown className="size-3.5" />
          Expand
        </ViewButton>
        <ViewButton
          label="Collapse all"
          disabled={searchActive || allCollapsed}
          onClick={() => setCollapsed(allContainers)}
        >
          <ChevronsDownUp className="size-3.5" />
          Collapse
        </ViewButton>
        <ViewButton label="Reveal selection" disabled={selection.length === 0} onClick={revealSelection}>
          <Focus className="size-3.5" />
          Reveal
        </ViewButton>
        {search ? (
          <span className="ml-auto whitespace-nowrap text-[10px] tabular-nums text-muted-foreground">
            {search.matches.size} {search.matches.size === 1 ? 'match' : 'matches'} · {flat.length} rows
          </span>
        ) : null}
      </div>
    </div>
    <div className="py-1 text-[13px]" role="tree" aria-label="Layers" aria-multiselectable="true">
      {flat.map((row) => (
        <Row
          key={row.node.id}
          row={row}
          labels={labels}
          query={query}
          selection={selection}
          tabbable={activeId === row.node.id}
          open={searchActive || !collapsed.has(row.node.id)}
          renaming={renamingId === row.node.id}
          drop={drop}
          registerRef={(el) => {
            if (el) rowRefs.current.set(row.node.id, el);
            else rowRefs.current.delete(row.node.id);
          }}
          onFocusRow={() => setFocusedId(row.node.id)}
          onKeyNav={onKeyNav}
          onToggleOpen={() => setOpen(row.node.id, collapsed.has(row.node.id))}
          onStartRename={() => setRenamingId(row.node.id)}
          onEndRename={() => setRenamingId(null)}
          onSelect={onSelect}
          onContextMenu={(id, event) => {
            event.preventDefault();
            if (!selection.includes(id)) onSelect(id);
            setContextMenu({ id, x: event.clientX, y: event.clientY });
          }}
          onOpenMenu={(id, anchor) => {
            if (!selection.includes(id)) onSelect(id);
            setContextMenu({ id, x: anchor.left, y: anchor.bottom + 4 });
          }}
          onRename={onRename}
          onDragStart={(id, e) => {
            setDraggedId(id);
            writeDragItem(e.dataTransfer, { type: 'node', id });
          }}
          onDragOver={(e) => {
            const target = targetFor(row, e);
            if (!target) {
              setDrop(null);
              return;
            }
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'move';
            setDrop(target);
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const item = readDragItem(e.dataTransfer);
            const id = item?.type === 'node' ? item.id : draggedId;
            const target = targetFor(row, e);
            if (id && target) onMove(id, target.parentId, target.index);
            clearDrag();
          }}
          onDragEnd={clearDrag}
        />
      ))}
    </div>
    {included && flat.length === 0 ? (
      <p className="px-3 py-5 text-center text-[11px] leading-5 text-muted-foreground">
        No layers match “{query}”. Try a block type, label, or node ID.
      </p>
    ) : null}
    {rootEmpty ? (
      <p className="px-3 py-2 text-[11px] leading-5 text-muted-foreground">
        This step is empty. Use the component bar on the canvas to add your first block.
      </p>
    ) : null}
    <NodeContextMenu
      state={contextMenu}
      root={root}
      labels={labels}
      handlers={handlers}
      onClose={() => setContextMenu(null)}
      onRename={(id) => setRenamingId(id)}
    />
    </>
  );
}

function Row({
  row,
  labels,
  query,
  selection,
  tabbable,
  open,
  renaming,
  drop,
  registerRef,
  onFocusRow,
  onKeyNav,
  onToggleOpen,
  onStartRename,
  onEndRename,
  onSelect,
  onContextMenu,
  onOpenMenu,
  onRename,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: {
  row: FlatRow;
  labels: AuthoringDoc['labels'];
  query: string;
  selection: readonly string[];
  tabbable: boolean;
  open: boolean;
  renaming: boolean;
  drop: TreeDrop | null;
  registerRef: (el: HTMLDivElement | null) => void;
  onFocusRow: () => void;
  onKeyNav: (row: FlatRow, e: React.KeyboardEvent<HTMLDivElement>) => void;
  onToggleOpen: () => void;
  onStartRename: () => void;
  onEndRename: () => void;
  onSelect: (id: string, mode?: 'replace' | 'toggle' | 'range') => void;
  onContextMenu: (id: string, event: React.MouseEvent<HTMLDivElement>) => void;
  onOpenMenu: (id: string, anchor: DOMRect) => void;
  onRename: (id: string, name: string) => void;
  onDragStart: (id: string, e: React.DragEvent<HTMLElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
}): React.JSX.Element {
  const renameCancelledRef = useRef(false);
  const { node, index, visibleIndex, visibleSiblingCount, depth } = row;
  const Icon = BLOCK_ICON[node.block];
  const hasChildren = isContainer(node) && node.children.length > 0;
  const selected = selection.includes(node.id);
  const label = nodeLabel(node, labels);
  // Names live on the AUTHORING DOC, not the block — `BlockBase` has no `name`, and it is not
  // getting one for a dashboard tree's sake. An unnamed node derives its summary instead.
  const named = Boolean(labels?.[node.id]);
  // The root (index -1) cannot be moved, duplicated or deleted — it is the document.
  const isRoot = index < 0;

  return (
    <div
      ref={registerRef}
      role="treeitem"
      aria-selected={selected}
      aria-level={depth + 1}
      aria-setsize={isRoot ? 1 : visibleSiblingCount}
      aria-posinset={isRoot ? 1 : visibleIndex + 1}
      aria-expanded={hasChildren ? open : undefined}
      tabIndex={tabbable ? 0 : -1}
      onFocus={onFocusRow}
      onClick={(event) => onSelect(node.id, event.shiftKey ? 'range' : event.metaKey || event.ctrlKey ? 'toggle' : 'replace')}
      onContextMenu={(event) => onContextMenu(node.id, event)}
      onDoubleClick={() => !isRoot && onStartRename()}
      onKeyDown={(e) => {
        if (renaming) return;
        onKeyNav(row, e);
      }}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={cn(
        'group relative flex cursor-default items-center gap-1 rounded-md py-1 pr-1 outline-none transition-colors',
        'focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring',
        selected ? 'bg-secondary font-medium text-foreground' : 'text-muted-foreground hover:bg-secondary/60',
        drop?.rowId === node.id && drop.position === 'inside' && 'bg-indigo-500/10 ring-1 ring-inset ring-indigo-500',
        drop?.rowId === node.id && drop.position === 'before' && 'before:absolute before:inset-x-1 before:top-0 before:h-0.5 before:bg-indigo-500',
        drop?.rowId === node.id && drop.position === 'after' && 'after:absolute after:inset-x-1 after:bottom-0 after:h-0.5 after:bg-indigo-500'
      )}
      // Preserve hierarchy without letting a pathological depth consume the row's action space.
      // `aria-level` above carries the exact depth; indentation is only the visual hint.
      style={{ paddingLeft: 8 + Math.min(depth, 6) * 12 }}
    >
      {isRoot ? (
        <span className="w-3" />
      ) : (
        <span
          draggable
          title={`Drag ${label}`}
          className="-ml-1 cursor-grab text-muted-foreground/40 opacity-0 group-hover:opacity-100 active:cursor-grabbing"
          onClick={(e) => e.stopPropagation()}
          onDragStart={(e) => {
            e.stopPropagation();
            onDragStart(node.id, e);
          }}
          onDragEnd={onDragEnd}
        >
          <GripVertical className="size-3" />
        </span>
      )}
      {hasChildren ? (
        <button
          type="button"
          // Roving focus owns keyboard expand/collapse (←/→); this button is a pointer affordance and
          // stays OUT of the tab order so the tree keeps its single tab stop.
          tabIndex={-1}
          aria-hidden="true"
          aria-label={open ? 'Collapse' : 'Expand'}
          className="-m-1 rounded p-1 focus-visible:ring-1 focus-visible:ring-ring"
          onClick={(e) => {
            e.stopPropagation();
            onToggleOpen();
          }}
        >
          {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        </button>
      ) : (
        <span className="w-3" />
      )}
      <Icon className="size-3.5 shrink-0" />

      {renaming ? (
        <input
          autoFocus
          aria-label={`Rename ${label}`}
          defaultValue={named ? label : ''}
          placeholder={label}
          className="min-w-0 flex-1 rounded border bg-transparent px-1 outline-none focus:border-indigo-500"
          onClick={(e) => e.stopPropagation()}
          onFocus={() => { renameCancelledRef.current = false; }}
          onBlur={(e) => {
            if (!renameCancelledRef.current) onRename(node.id, e.target.value);
            renameCancelledRef.current = false;
            onEndRename();
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              e.preventDefault();
              renameCancelledRef.current = true;
              onEndRename();
            }
          }}
        />
      ) : (
        <span data-layer-label="" className={cn('min-w-0 flex-1 truncate', !named && 'text-muted-foreground')}>
          <HighlightedLabel label={label} query={query} />
        </span>
      )}

      {/* The row stays readable; the complete action set is one accessible menu away. Reorder also
          remains available through drag and ⌘/⌥+↑↓, so four repeated icon buttons do not consume the
          label at every depth. */}
      {!isRoot && !renaming ? (
        <span
          className={cn(
            'flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100',
            selected && 'opacity-100'
          )}
        >
          <RowButton
            label={`Actions for ${label}`}
            onClick={(anchor) => onOpenMenu(node.id, anchor)}
          >
            <MoreHorizontal className="size-3.5" />
          </RowButton>
        </span>
      ) : null}
    </div>
  );
}

function ViewButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex h-7 items-center gap-1 rounded px-1.5 text-[10px] text-muted-foreground hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-30"
    >
      {children}
    </button>
  );
}

/** One tree-row verb. `stopPropagation` so pressing it never also selects the row. Hit target is
 *  padded to ~24px (`p-1.5` + a `size-3` icon) even though the icon stays small — C3. */
function RowButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: (anchor: DOMRect) => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      className="grid size-6 place-items-center rounded outline-none hover:bg-background focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-30 disabled:hover:bg-transparent"
      onClick={(e) => {
        e.stopPropagation();
        onClick(e.currentTarget.getBoundingClientRect());
      }}
    >
      {children}
    </button>
  );
}

function HighlightedLabel({ label, query }: { label: string; query: string }): React.JSX.Element {
  const needle = query.trim();
  const index = needle ? label.toLocaleLowerCase().indexOf(needle.toLocaleLowerCase()) : -1;
  if (index < 0) return <>{label}</>;
  return (
    <>
      {label.slice(0, index)}
      <mark className="rounded-sm bg-amber-200 px-0.5 text-inherit dark:bg-amber-700/70">
        {label.slice(index, index + needle.length)}
      </mark>
      {label.slice(index + needle.length)}
    </>
  );
}
